import { toJS } from "mobx";
import { BackgroundConfigType, ImageToImageConfigType, SizeConfigType } from "../../../models/image-to-image";
import { TextToImageConfigType } from "../../../models/text-to-image";

export type PostMessageConfig = Record<string, unknown>;

type SizePostMessageConfig = {
	aspect_ratio?: { value: string[] };
	custom_size?: { width?: number; height?: number };
	medium_destination?: { platform?: string; destinations?: string[] };
	increase_resolution?: { scale: string[] };
	crop?: boolean;
};

type BackgroundPostMessageConfig = {
	generate_by_text?: {
		prompt?: string;
		num_results?: number;
		mode?: string;
		refine_prompt?: boolean;
	};
	generate_by_image?: {
		ref_image_url?: string;
		num_results?: number;
	};
	solid?: { color?: string };
	blur?: boolean;
	remove?: boolean;
	expand?: boolean;
};

type ObjectManipulationPostMessageConfig = {
	type?: "erase" | "generative_fill";
	prompt?: string;
	tailored_model_id?: string;
	num_results?: number;
	mask_type?: string;
};

export type ImageToImagePostMessageConfig = {
	num_results?: number;
	size?: SizePostMessageConfig;
	manual_placement?: {
		width?: number;
		height?: number;
		canvas_size?: number[];
	};
	background?: BackgroundPostMessageConfig;
	foreground_placement?: { placements: string[] };
	object_manipulation?: ObjectManipulationPostMessageConfig;
};

export type TextToImagePostMessageConfig = {
	model?: {
		id?: string | string[];
		name?: string;
		type?: string;
		version?: string;
	};
	tailored_model?: {
		id: string | string[];
		influence?: number;
		training_version?: string;
	};
	aspect_ratio?: string;
	medium?: string;
	prompt?: string;
	num_results?: number;
	image_reference?: {
		image?: string;
		urls?: string[];
		scale?: number;
		mode?: string;
	};
	advanced?: {
		steps?: number;
		seed?: number;
		negative_prompt?: string;
		guidance_scale?: number;
		prompt_enhancement?: boolean;
	};
};

const extractSizeConfig = (size: SizeConfigType): SizePostMessageConfig | null => {
	const sizeConfig: SizePostMessageConfig = {};

	if (size.aspect_ratio?.selected && size.aspect_ratio.value?.length) {
		sizeConfig.aspect_ratio = { value: [...size.aspect_ratio.value] };
	}
	if (size.custom_size?.selected) {
		sizeConfig.custom_size = { width: size.custom_size.width, height: size.custom_size.height };
	}
	if (size.medium_destination?.selected) {
		sizeConfig.medium_destination = {
			platform: size.medium_destination.platform?.key,
			destinations: size.medium_destination.destinations?.map((d) => d.name) ?? [],
		};
	}
	if (size.increase_resolution?.selected && size.increase_resolution.value?.length) {
		sizeConfig.increase_resolution = { scale: [...size.increase_resolution.value] };
	}
	if (size.crop) {
		sizeConfig.crop = true;
	}

	return Object.keys(sizeConfig).length > 0 ? sizeConfig : null;
};

const extractBackgroundConfig = (background: BackgroundConfigType): BackgroundPostMessageConfig | null => {
	const bgConfig: BackgroundPostMessageConfig = {};

	if (background.generateByText?.selected) {
		bgConfig.generate_by_text = {
			prompt: background.generateByText.prompt,
			num_results: background.generateByText.num_results,
			...(background.generateByText.mode && { mode: background.generateByText.mode }),
			...(background.generateByText.refine_prompt !== undefined && {
				refine_prompt: background.generateByText.refine_prompt,
			}),
		};
	}
	if (background.generateByImage?.selected) {
		bgConfig.generate_by_image = {
			ref_image_url: background.generateByImage.ref_image_url,
			num_results: background.generateByImage.num_results,
		};
	}
	if (background.solid?.selected) {
		bgConfig.solid = { color: background.solid.color_code };
	}
	if (background.blur) {
		bgConfig.blur = true;
	}
	if (background.remove) {
		bgConfig.remove = true;
	}
	if (background.expand) {
		bgConfig.expand = true;
	}

	return Object.keys(bgConfig).length > 0 ? bgConfig : null;
};

export const extractImageToImageConfig = (config: ImageToImageConfigType): ImageToImagePostMessageConfig => {
	const extractedConfig: ImageToImagePostMessageConfig = {};

	if (config.num_results) extractedConfig.num_results = config.num_results;

	if (config.size) {
		const sizeConfig = extractSizeConfig(config.size);
		if (sizeConfig) extractedConfig.size = sizeConfig;
	}

	if (config.manual_placement) {
		const { width, height, canvas_size } = config.manual_placement;
		const manualConfig: ImageToImagePostMessageConfig["manual_placement"] = {};
		if (width) manualConfig.width = width;
		if (height) manualConfig.height = height;
		if (canvas_size) manualConfig.canvas_size = [...canvas_size];
		if (Object.keys(manualConfig).length > 0) extractedConfig.manual_placement = manualConfig;
	}

	if (config.background) {
		const bgConfig = extractBackgroundConfig(config.background);
		if (bgConfig) extractedConfig.background = bgConfig;
	}

	if (config.foreground_placement?.placements?.selected && config.foreground_placement.placements.values?.length) {
		extractedConfig.foreground_placement = { placements: [...config.foreground_placement.placements.values] };
	}

	if (config.object_manipulation?.selected) {
		const objConfig: ObjectManipulationPostMessageConfig = {};
		if (config.object_manipulation.erase) {
			objConfig.type = "erase";
		} else if (config.object_manipulation.gen_fill) {
			objConfig.type = "generative_fill";
			if (config.object_manipulation.prompt) objConfig.prompt = config.object_manipulation.prompt;
			if (config.object_manipulation.tailored_model_id) {
				objConfig.tailored_model_id = config.object_manipulation.tailored_model_id;
			}
		}
		if (config.object_manipulation.num_results) objConfig.num_results = config.object_manipulation.num_results;
		if (config.object_manipulation.mask_type) objConfig.mask_type = config.object_manipulation.mask_type;
		if (Object.keys(objConfig).length > 0) extractedConfig.object_manipulation = objConfig;
	}

	return extractedConfig;
};

export const extractTextToImageConfig = (config: TextToImageConfigType): TextToImagePostMessageConfig => {
	const extractedConfig: TextToImagePostMessageConfig = {};

	if (config.model_id || config.model_name || config.model) {
		extractedConfig.model = {
			...(config.model_id && { id: config.model_id }),
			...(config.model_name && { name: config.model_name }),
			...(config.model && { type: config.model }),
			...(config.model_version && { version: config.model_version }),
		};
	}

	if (config.tailored_model_id) {
		extractedConfig.tailored_model = {
			id: config.tailored_model_id,
			...(config.tailored_model_influence && { influence: config.tailored_model_influence }),
			...(config.training_version && { training_version: config.training_version }),
		};
	}

	if (config.aspect_ratio) extractedConfig.aspect_ratio = config.aspect_ratio;
	if (config.medium) extractedConfig.medium = config.medium;
	if (config.prompt) extractedConfig.prompt = config.prompt;
	if (config.num_results) extractedConfig.num_results = config.num_results;

	if (config.image_prompt_file || config.image_prompt_urls?.length) {
		extractedConfig.image_reference = {
			...(config.image_prompt_file && { image: config.image_prompt_file }),
			...(config.image_prompt_urls?.length && { urls: [...config.image_prompt_urls] }),
			...(config.image_prompt_scale && { scale: config.image_prompt_scale }),
			...(config.image_prompt_mode && { mode: config.image_prompt_mode }),
		};
	}

	const advancedSettings: TextToImagePostMessageConfig["advanced"] = {};
	if (config.steps_num) advancedSettings.steps = config.steps_num;
	if (config.seed) advancedSettings.seed = config.seed;
	if (config.negative_prompt) advancedSettings.negative_prompt = config.negative_prompt;
	if (config.text_guidance_scale) advancedSettings.guidance_scale = config.text_guidance_scale;
	if (config.prompt_enhancement !== undefined) advancedSettings.prompt_enhancement = config.prompt_enhancement;

	if (Object.keys(advancedSettings).length > 0) {
		extractedConfig.advanced = advancedSettings;
	}

	return extractedConfig;
};

export const toPlainConfig = <T>(config: T): T => toJS(config);
